import qs from 'qs'

import { updateBiometricToken, deleteBiometricToken, shouldUseBiometric } from './biometric'

import {
  SPARCK_USER_TOKEN,
  SPARCK_USER_ID,
  SPARCK_USER_TOKEN_ID,
} from 'constants/app'
import { API_BASE_URL } from 'core/environments'
import { API_ROUTES } from 'core/api'
import { history } from 'core/history'
import { ROUTES_PATHS } from 'core/routes'
import SnackbarUtils from 'core/SnackbarConfiguration'
import { t } from 'i18n'
import { getUserTokenFromLocalStorage } from 'utils'
import { getSpoofedCompanyFromLocalStorage } from 'utils/admin'

// Module-level (not per-request) so a burst of same-tick token_expired
// responses (several queries firing on the same page navigation) only
// surfaces one notice.
let sessionExpiredNoticeShown = false

export type RequestOptionsType = {
  path: string
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  queryParams?: any
  pathParams?: any
  body?: any
}

// used in admin mode
export const getCompanyId = () => {
  const company = getSpoofedCompanyFromLocalStorage()

  return company?.id ?? ''
}

export const LS_BUILD_KEY = 'current.build.version'
export const currentBuildVersion = () => localStorage.getItem(LS_BUILD_KEY)

export const defaultHeaders = (token, body) => ({
  headers: {
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    'Access-Control-Expose-Headers': '*',
    ...(!(body instanceof FormData) && {
      'Content-Type': 'application/json',
    }),
    ...(currentBuildVersion() && {
      'X-Front-End-Build-Version': currentBuildVersion(),
    }),
  },
})

export const validateAPIResponse = async response => {
  // Handle 204 No Content responses (e.g., successful DELETE operations)
  // 204 responses have no body, so we shouldn't try to parse JSON
  const data = response.status === 204 ? {} : await response.json()

  const refreshToken = response.headers.get('X-Refreshed-Token')

  if (refreshToken) {
    localStorage.setItem(SPARCK_USER_TOKEN, refreshToken)

    if (shouldUseBiometric()) {
      updateBiometricToken(refreshToken)
    }
  }

  const reloadRequired =
    currentBuildVersion() && response.status === 428 && data.reload

  if (reloadRequired) {
    localStorage.removeItem(SPARCK_USER_TOKEN)
    localStorage.removeItem(SPARCK_USER_TOKEN_ID)
    localStorage.removeItem(SPARCK_USER_ID)

    localStorage.setItem(LS_BUILD_KEY, data.versions.current)

    window.location.reload()
  }

  if (response && response.status >= 400) {
    if (
      !(
        data &&
        data.error_type === 'user_not_authorized' &&
        !getUserTokenFromLocalStorage()
      )
    ) {
      if (
        data &&
        data.errors &&
        data.error_type &&
        ![
          'minimum_participants_needed',
          'token_expired',
          'record_not_unique',
        ].includes(data.error_type) &&
        !data.token_expired
      ) {
        data.errors.forEach(error => {
          SnackbarUtils.error(error)
        })
      }
      if (data.error_type && data.error_type === 'token_expired') {
        if (!sessionExpiredNoticeShown) {
          sessionExpiredNoticeShown = true
          SnackbarUtils.warning(t('login.snackbar.sessionExpired'))
          setTimeout(() => {
            sessionExpiredNoticeShown = false
          }, 5000)
        }

        history.push(ROUTES_PATHS.login)
        localStorage.removeItem(SPARCK_USER_TOKEN)
        localStorage.removeItem(SPARCK_USER_ID)

        if (shouldUseBiometric()) {
          deleteBiometricToken()
        }

        throw new Error('token_expired')
      }
    }
  }

  return data
}

const handlePathParams = (url, pathParams) => {
  for (const match of url.pathname.matchAll(/(:\w+)/g)) {
    const matchName = match[0].replace(':', '')
    const matchValue = pathParams[matchName]
    url.pathname = url.pathname.replace(match[0], matchValue ? matchValue : '')
  }

  return url
}

const handleSearchParams = (queryParams, method) => {
  const companyId = getCompanyId()

  const newQueryParams =
    companyId && method === 'GET'
      ? { ...queryParams, company_id: companyId }
      : queryParams

  const searchParams = qs.stringify(newQueryParams, { arrayFormat: 'comma' })

  return searchParams
}

const getRequestURL = (requestOptions: RequestOptionsType) => {
  const { path, queryParams = {}, pathParams = {}, method } = requestOptions

  const url: URL = handlePathParams(
    new URL(`${API_BASE_URL}${path}`),
    pathParams,
  )

  const searchParams: any = handleSearchParams(queryParams, method)

  url.search = searchParams.toString()
  url.toString().replace(/(\/)\W+/g, '/')

  return url.toString()
}

const getRequestBody = (method, body) => {
  const companyId = getCompanyId()

  if (body instanceof FormData) {
    return body
  }

  if (body) {
    if (companyId) body.company_id = companyId

    return JSON.stringify(body)
  }

  if (companyId && method !== 'GET') {
    return JSON.stringify({ company_id: companyId })
  }

  return null
}

import { SPARCK_REFRESH_USER_TOKEN } from 'constants/app'

const tryRefreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(SPARCK_REFRESH_USER_TOKEN)
  if (!refreshToken) return null

  const url = `${API_BASE_URL}${API_ROUTES.refreshToken}`

  try {
    const buildVersion = currentBuildVersion()
    const resp = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(buildVersion ? { 'X-Front-End-Build-Version': buildVersion } : {}),
      },
      // @ts-ignore
      encoding: null,
      mode: 'cors',
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    const json = await resp.json().catch(() => null)
    const newAccessToken = json?.data?.access_token

    if (resp.ok && newAccessToken) {
      localStorage.setItem(SPARCK_USER_TOKEN, newAccessToken)

      if (shouldUseBiometric()) {
        updateBiometricToken(newAccessToken)
      }
      return newAccessToken
    }
  } catch (_) {
    // swallow; will return null
  }

  return null
}

const rebuildFetchOptions = (
  requestOptions: RequestOptionsType,
  tokenOverride?: string,
): RequestInit => {
  const { method, body } = requestOptions
  const opts = {
    ...defaultHeaders(tokenOverride ?? getUserTokenFromLocalStorage(), body),
    // @ts-ignore
    encoding: null,
    // @ts-ignore
    mode: 'cors',
    method: method,
    body: getRequestBody(method, body),
  } as unknown as RequestInit

  return opts
}

export const APIRequest = async (requestOptions: RequestOptionsType) => {
  const url = getRequestURL(requestOptions)
  const initialResponse = await fetch(url, rebuildFetchOptions(requestOptions))

  if (initialResponse.ok) {
    return validateAPIResponse(initialResponse)
  }

  // Peek error payload to decide if we should refresh token
  let errorData: any = null
  try {
    errorData = await initialResponse.clone().json()
  } catch (_) {
    errorData = null
  }

  const isTokenExpiredError =
    initialResponse.status === 401 || errorData?.error_type === 'token_expired'

  if (isTokenExpiredError) {
    const refreshedToken = await tryRefreshAccessToken()
    if (refreshedToken) {
      const retryResponse = await fetch(
        url,
        rebuildFetchOptions(requestOptions, refreshedToken),
      )
      if (retryResponse.ok) {
        return validateAPIResponse(retryResponse)
      }
      return validateAPIResponse(retryResponse)
    }
  }

  return validateAPIResponse(initialResponse)
}